import {
  shouldAdvanceOnAcceptedTurn,
  shouldFinishOnAcceptedTurn,
} from './shared-responder-v2-core-advance-decision.helpers';
import { withoutDraftSource } from './shared-responder-v2-core-draft-source.helpers';
import {
  applyEditSubmission,
  applyTurnEdit,
  canEditTurn,
  revertRejectedEdit,
} from './shared-responder-v2-core-edit.helpers';
import {
  QUESTION_ALREADY_COMPLETED_FAILURE,
  RESPONDER_FAILURE_KINDS,
} from './shared-responder-v2-core-failure-kind.consts';
import {
  commitFollowUpReply,
  isActiveDraftComplete,
  isReplyingToFollowUp,
  resolveActiveDraft,
} from './shared-responder-v2-core-follow-up-reply.helpers';
import type {
  QuestionLiveResultsResult,
  QuestionTurnResult,
} from './shared-responder-v2-core-gateway.types';
import {
  applyNextQuestionRequired,
  rejectSkipAsRequired,
} from './shared-responder-v2-core-required-sync.helpers';
import {
  advance,
  applyThreadActions,
  exitEdit,
  withThread,
} from './shared-responder-v2-core-session-machine.helpers';
import {
  appendAssistantTurns,
  appendResponderTurn,
  hasUnansweredFollowUp,
} from './shared-responder-v2-core-session-turns.helpers';
import type { TurnEditSubmission } from './shared-responder-v2-core-session.events';
import { selectCurrentStep } from './shared-responder-v2-core-session.selectors';
import type { ResponderSessionState } from './shared-responder-v2-core-session.types';
import {
  THREAD_STATUSES,
  TURN_INTENTS,
  TURN_ROLES,
} from './shared-responder-v2-core-thread.consts';
import type {
  ResponderAnswer,
  ResponderThread,
  ResponderTurn,
} from './shared-responder-v2-core-thread.types';

/**
 * An accepted edit makes the edited question current again: the backend has
 * discarded whatever followed it — including the next question it handed out —
 * and re-routed from the new answer, so the cursor continues from here (to a
 * follow-up, or to the newly routed question) instead of returning to the step
 * the edit started from.
 */
function resumeFromEditedQuestion(
  state: ResponderSessionState,
  questionId: string
): ResponderSessionState {
  const thread = state.threads[questionId];

  if (thread === undefined || thread.editRollback === null) {
    return state;
  }

  const stepIndex = state.steps.findIndex(
    (step) => step.questionId === questionId
  );

  return stepIndex === -1
    ? state
    : { ...state, editing: { mode: 'none' }, currentStepIndex: stepIndex };
}

const ALREADY_COMPLETED_RESULT: QuestionTurnResult = {
  sent: null,
  replyFeedback: null,
  reply: null,
  actions: [],
  nextQuestionId: null,
  isComplete: true,
  surveyProgress: 0,
  isSurveyComplete: false,
  thankYouText: null,
};

export function acceptThreadMessage(
  state: ResponderSessionState,
  questionId: string,
  result: QuestionTurnResult,
  liveResults: QuestionLiveResultsResult | null = null
): ResponderSessionState {
  const accepted = applyNextQuestionRequired(
    applyThreadActions(
      withThread(
        resumeFromEditedQuestion(state, questionId),
        questionId,
        (thread) => appendAssistantTurns(thread, result, liveResults)
      ),
      questionId,
      result
    ),
    result
  );

  return shouldAdvanceOnAcceptedTurn(accepted, questionId, result) ||
    shouldFinishOnAcceptedTurn(accepted, questionId, result)
    ? advance(accepted)
    : accepted;
}

/**
 * Records a rejected turn — as a failure, or, for a rejected *edit*, as no
 * change at all.
 *
 * An edit is the one submission the backend can refuse on grounds that leave
 * the question perfectly answered (`editQuestionMessage` refuses once a later
 * question is answered), so the original answer still stands. Marking the
 * thread `Failed` there produced a question that could never be finished — the
 * rail lost its tick, Continue refuses anything but `Closed`, and the offered
 * retry re-posted the same message into the same wall. Restoring the snapshot
 * instead leaves the session exactly as the backend still sees it; the caller
 * is what tells the responder their change did not take.
 */
function resolvePrimaryAnswer(thread: ResponderThread): ResponderAnswer | null {
  const primaryTurn = thread.turns.find(
    (turn) =>
      turn.role === TURN_ROLES.Responder &&
      turn.intent === TURN_INTENTS.PrimaryAnswer
  );

  if (primaryTurn === undefined) {
    return thread.draft ?? thread.answer;
  }

  return {
    kind: 'text',
    text: primaryTurn.text,
    transcript: primaryTurn.transcript,
  };
}

function withoutOptimisticAnswerTurn(
  thread: ResponderThread,
  failedAnswer: ResponderAnswer
): readonly ResponderTurn[] {
  const lastTurn = thread.turns.at(-1);
  const isOptimisticAnswerTurn =
    lastTurn?.role === TURN_ROLES.Responder &&
    (failedAnswer.kind === 'text'
      ? lastTurn.text === failedAnswer.text
      : lastTurn.intent === TURN_INTENTS.FollowUpAnswer);

  return isOptimisticAnswerTurn ? thread.turns.slice(0, -1) : thread.turns;
}

function restoreFailedAnswerToDraft(
  thread: ResponderThread,
  message: string
): ResponderThread {
  const failedAnswer = thread.answer;

  if (failedAnswer === null) {
    return { ...thread, status: THREAD_STATUSES.Failed, error: message };
  }

  const turns = withoutOptimisticAnswerTurn(thread, failedAnswer);
  const isFollowUpReply = hasUnansweredFollowUp({ ...thread, turns });

  return isFollowUpReply
    ? {
        ...thread,
        turns,
        followUpDraft: failedAnswer,
        answer: resolvePrimaryAnswer(thread),
        status: THREAD_STATUSES.Failed,
        error: message,
      }
    : {
        ...thread,
        turns,
        draft: failedAnswer,
        answer: null,
        status: THREAD_STATUSES.Failed,
        error: message,
      };
}

function applyThreadFailure(
  thread: ResponderThread,
  message: string
): ResponderThread {
  return message === RESPONDER_FAILURE_KINDS.AnswerNotProcessed
    ? restoreFailedAnswerToDraft(thread, message)
    : { ...thread, status: THREAD_STATUSES.Failed, error: message };
}

export function failThread(
  state: ResponderSessionState,
  questionId: string,
  message: string
): ResponderSessionState {
  if (message === RESPONDER_FAILURE_KINDS.QuestionRequired) {
    return rejectSkipAsRequired(state, questionId);
  }

  const failed = state.threads[questionId];
  const isRejectedEdit = failed !== undefined && failed.editRollback !== null;

  if (message === QUESTION_ALREADY_COMPLETED_FAILURE && !isRejectedEdit) {
    return acceptThreadMessage(state, questionId, ALREADY_COMPLETED_RESULT);
  }

  const withFailure = withThread(state, questionId, (thread) =>
    isRejectedEdit
      ? revertRejectedEdit(thread)
      : applyThreadFailure(thread, message)
  );

  const isStillEditing =
    withFailure.editing.mode === 'editing' &&
    withFailure.editing.questionId === questionId;

  return isRejectedEdit && isStillEditing ? exitEdit(withFailure) : withFailure;
}

export function commitCurrentDraft(
  state: ResponderSessionState
): ResponderSessionState {
  const step = state.steps[state.currentStepIndex];

  if (step === undefined) {
    return state;
  }

  const thread = state.threads[step.questionId];

  if (thread === undefined || thread.answer !== null) {
    return state;
  }

  const submitted = submitAnswer(state, step.questionId);

  return submitted !== state || thread.draftSource === undefined
    ? submitted
    : withThread(state, step.questionId, withoutDraftSource);
}

export function submitAnswer(
  state: ResponderSessionState,
  questionId: string
): ResponderSessionState {
  const submittedDraft = resolveActiveDraft(state, questionId);

  if (!isActiveDraftComplete(state, questionId) || submittedDraft === null) {
    return state;
  }

  if (isReplyingToFollowUp(state, questionId)) {
    return withThread(state, questionId, (current) =>
      commitFollowUpReply(current, submittedDraft)
    );
  }

  const { editing } = state;

  if (editing.mode === 'editing' && editing.questionId === questionId) {
    const { draftBeforeEdit } = editing;

    return withThread(state, questionId, (current) =>
      applyEditSubmission(current, { answer: submittedDraft, draftBeforeEdit })
    );
  }

  return withThread(state, questionId, (current) =>
    appendResponderTurn({
      ...withoutDraftSource(current),
      answer: submittedDraft,
      status: THREAD_STATUSES.AwaitingAssistant,
      requestSeq: current.requestSeq + 1,
    })
  );
}

export function submitTurnEdit(
  state: ResponderSessionState,
  { questionId, turnId, text }: TurnEditSubmission
): ResponderSessionState {
  if (
    text.trim() === '' ||
    selectCurrentStep(state)?.questionId !== questionId ||
    !canEditTurn(state, turnId)
  ) {
    return state;
  }

  return withThread(state, questionId, (thread) =>
    applyTurnEdit(thread, { turnId, text })
  );
}
